import React, { useState, useEffect, useRef } from 'react';
import { RotateCw, RefreshCw, ZoomIn, ZoomOut, Maximize, Play, Pause, Compass, Box } from 'lucide-react';

const THREE_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
const ORBIT_CONTROLS_CDN = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js';
const GLTF_LOADER_CDN = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js';
const DRACO_LOADER_CDN = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/DRACOLoader.js';

let threeScriptPromise = null;
const loadThreeScripts = () => {
    if (typeof window === 'undefined') return Promise.resolve(null);
    if (window.THREE && window.THREE.OrbitControls && window.THREE.GLTFLoader) return Promise.resolve(window.THREE);
    if (threeScriptPromise) return threeScriptPromise;

    threeScriptPromise = new Promise((resolve) => {
        const loadScript = (src) => {
            return new Promise((res, rej) => {
                const existing = document.querySelector(`script[src="${src}"]`);
                if (existing) {
                    if (window.THREE) return res();
                    existing.addEventListener('load', () => res());
                    existing.addEventListener('error', () => rej());
                    return;
                }
                const s = document.createElement('script');
                s.src = src;
                s.async = true;
                s.onload = () => res();
                s.onerror = () => rej(new Error(`Failed to load ${src}`));
                document.head.appendChild(s);
            });
        };

        loadScript(THREE_CDN)
            .then(() => loadScript(ORBIT_CONTROLS_CDN))
            .then(() => loadScript(GLTF_LOADER_CDN))
            .then(() => loadScript(DRACO_LOADER_CDN))
            .then(() => resolve(window.THREE))
            .catch(() => resolve(window.THREE));
    });

    return threeScriptPromise;
};

/**
 * RealProduct360Viewer
 * 
 * Supports:
 * 1. Multi-Frame 360° Photo Sequences (Array of authentic product angle photos)
 * 2. Real Product Single Image Axial Turntable (Smooth 360 rotation with perspective & lighting)
 * 3. 3D GLB/glTF Model (When a 3D asset is available)
 */
export default function RealProduct360Viewer({
    frames = [],
    initialImage = '',
    glbUrl = null,
    productName = 'المنتج الفاخر',
    isRTL = true,
    scaleFactor = 1.0,
    lightingPreset = 'studio',
    autoRotateDefault = true,
    className = '',
    style = {}
}) {
    const containerRef = useRef(null);
    const canvas3dRef = useRef(null);

    // Mode: 'frames360' | '3d_model' | 'turntable'
    const [viewMode, setViewMode] = useState('turntable');
    const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartFrame, setDragStartFrame] = useState(0);
    const [autoRotate, setAutoRotate] = useState(autoRotateDefault);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [loadedImagesCount, setLoadedImagesCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [activeAngleDeg, setActiveAngleDeg] = useState(0);

    // 3D Three.js Refs
    const rendererRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const modelGroupRef = useRef(null);
    const animFrameRef = useRef(null);

    // Normalize frames list
    const activeFrames = Array.isArray(frames) && frames.length > 0
        ? frames
        : (initialImage ? [initialImage] : []);

    // Total available frames for full 360
    const totalFrames = activeFrames.length >= 4 ? activeFrames.length : 36; // 36 angle steps for smooth continuous rotation

    // Determine initial mode
    useEffect(() => {
        if (glbUrl && glbUrl.trim().length > 0) {
            setViewMode('3d_model');
        } else if (activeFrames.length >= 4) {
            setViewMode('frames360');
        } else {
            setViewMode('turntable');
        }
    }, [glbUrl, activeFrames.length]);

    // Preload image frames for instantaneous 60fps spin
    useEffect(() => {
        if (!activeFrames.length) {
            setIsLoading(false);
            return;
        }

        let loaded = 0;
        const total = activeFrames.length;
        setIsLoading(true);

        activeFrames.forEach((src) => {
            const img = new Image();
            img.onload = () => {
                loaded += 1;
                setLoadedImagesCount(loaded);
                if (loaded >= total) {
                    setIsLoading(false);
                }
            };
            img.onerror = () => {
                loaded += 1;
                if (loaded >= total) {
                    setIsLoading(false);
                }
            };
            img.src = src;
        });
    }, [activeFrames]);

    // Auto-Rotate Interval for Multi-frame & Turntable
    useEffect(() => {
        if (!autoRotate || isDragging || viewMode === '3d_model') return;

        const interval = setInterval(() => {
            setCurrentFrameIndex((prev) => (prev + 1) % totalFrames);
            setActiveAngleDeg((prev) => (prev + (360 / totalFrames)) % 360);
        }, 80);

        return () => clearInterval(interval);
    }, [autoRotate, isDragging, totalFrames, viewMode]);

    // Mouse & Touch Drag Handlers
    const handleDragStart = (clientX) => {
        setAutoRotate(false);
        setIsDragging(true);
        setDragStartX(clientX);
        setDragStartFrame(currentFrameIndex);
    };

    const handleDragMove = (clientX) => {
        if (!isDragging) return;
        const deltaX = clientX - dragStartX;
        const sensitivity = 380;
        const frameOffset = Math.round((deltaX / sensitivity) * totalFrames);
        const newIndex = ((dragStartFrame - frameOffset) % totalFrames + totalFrames) % totalFrames;
        
        setCurrentFrameIndex(newIndex);
        setActiveAngleDeg(Math.round((newIndex / totalFrames) * 360));
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    // Zoom Controls
    const handleZoomIn = () => {
        setZoomLevel((prev) => Math.min(prev + 0.3, 2.5));
    };

    const handleZoomOut = () => {
        setZoomLevel((prev) => Math.max(prev - 0.3, 1.0));
    };

    const handleReset = () => {
        setCurrentFrameIndex(0);
        setActiveAngleDeg(0);
        setZoomLevel(1.0);
        setPanOffset({ x: 0, y: 0 });
        setAutoRotate(false);
        if (controlsRef.current) {
            controlsRef.current.reset();
        }
    };

    // Fullscreen Toggle
    const handleToggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen?.().catch(() => {});
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.().catch(() => {});
            setIsFullscreen(false);
        }
    };

    // 3D Three.js Initializer (Used when GLB is provided)
    useEffect(() => {
        if (viewMode !== '3d_model' || !glbUrl || !canvas3dRef.current) return;

        let isMounted = true;
        loadThreeScripts().then((THREE) => {
            if (!THREE || !isMounted || !canvas3dRef.current || !containerRef.current) return;

            const width = containerRef.current.clientWidth || 500;
            const height = containerRef.current.clientHeight || 500;

            const scene = new THREE.Scene();
            sceneRef.current = scene;

            const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
            camera.position.set(0, 1.2, 3.2);
            cameraRef.current = camera;

            const renderer = new THREE.WebGLRenderer({
                canvas: canvas3dRef.current,
                antialias: true,
                alpha: true,
                powerPreference: 'high-performance'
            });
            renderer.setSize(width, height);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            renderer.shadowMap.enabled = true;
            renderer.shadowMap.type = THREE.PCFSoftShadowMap;
            rendererRef.current = renderer;

            const controls = new THREE.OrbitControls(camera, renderer.domElement);
            controls.enableDamping = true;
            controls.dampingFactor = 0.05;
            controls.autoRotate = autoRotate;
            controls.autoRotateSpeed = 2.0;
            controls.maxDistance = 8;
            controls.minDistance = 1;
            controlsRef.current = controls;

            // Ambient & Studio Lights
            const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
            scene.add(ambientLight);

            const mainLight = new THREE.DirectionalLight(0xffeedd, 2.0);
            mainLight.position.set(5, 10, 7);
            mainLight.castShadow = true;
            scene.add(mainLight);

            const fillLight = new THREE.DirectionalLight(0xddeeff, 1.0);
            fillLight.position.set(-5, 5, -5);
            scene.add(fillLight);

            // Load Real GLB Model
            const loader = new THREE.GLTFLoader();
            if (THREE.DRACOLoader) {
                const draco = new THREE.DRACOLoader();
                draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.4.1/');
                loader.setDRACOLoader(draco);
            }

            loader.load(
                glbUrl,
                (gltf) => {
                    if (!isMounted) return;
                    const model = gltf.scene;
                    modelGroupRef.current = model;

                    const scale = Number(scaleFactor) || 1.0;
                    model.scale.set(scale, scale, scale);

                    const box = new THREE.Box3().setFromObject(model);
                    const center = box.getCenter(new THREE.Vector3());
                    model.position.sub(center);
                    model.position.y += (box.max.y - box.min.y) * 0.1;

                    scene.add(model);
                    setIsLoading(false);
                },
                undefined,
                (err) => {
                    console.warn('Could not load 3D GLB file, switching to 360 photo turntable:', err);
                    if (isMounted) setViewMode('turntable');
                }
            );

            const animate = () => {
                animFrameRef.current = requestAnimationFrame(animate);
                controls.update();
                renderer.render(scene, camera);
            };
            animate();
        });

        return () => {
            isMounted = false;
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            if (rendererRef.current) rendererRef.current.dispose();
        };
    }, [viewMode, glbUrl, scaleFactor, autoRotate]);

    // Active image source for multi-frame
    const activeFrameSrc = activeFrames.length > 0
        ? (activeFrames[currentFrameIndex % activeFrames.length] || activeFrames[0])
        : initialImage;

    // Simulated 3D axial perspective angle for single image turntable
    const rotationAngle = (currentFrameIndex / totalFrames) * 360;

    return (
        <div
            ref={containerRef}
            className={`real-product-360-container ${className}`}
            style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '1 / 1',
                maxHeight: '520px',
                borderRadius: '24px',
                background: 'radial-gradient(circle at 50% 40%, #171B26 0%, #0B0E14 100%)',
                border: '1px solid rgba(212, 175, 55, 0.25)',
                overflow: 'hidden',
                userSelect: 'none',
                touchAction: 'none',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.4), inset 0 0 40px rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isDragging ? 'grabbing' : 'grab',
                ...style
            }}
            onMouseDown={(e) => handleDragStart(e.clientX)}
            onMouseMove={(e) => handleDragMove(e.clientX)}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={(e) => {
                if (e.touches.length === 1) handleDragStart(e.touches[0].clientX);
            }}
            onTouchMove={(e) => {
                if (e.touches.length === 1) handleDragMove(e.touches[0].clientX);
            }}
            onTouchEnd={handleDragEnd}
        >
            {/* Ambient Lighting & Stage Floor Pedestal */}
            <div
                style={{
                    position: 'absolute',
                    bottom: '10%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '68%',
                    height: '24%',
                    background: 'radial-gradient(ellipse at center, rgba(212, 175, 55, 0.22) 0%, rgba(212, 175, 55, 0.05) 50%, transparent 80%)',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                    filter: 'blur(8px)',
                    zIndex: 1
                }}
            />

            {/* View Mode: 3D Model WebGL Canvas */}
            {viewMode === '3d_model' && glbUrl ? (
                <canvas
                    ref={canvas3dRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                        zIndex: 2
                    }}
                />
            ) : viewMode === 'frames360' ? (
                /* Mode 1: Multi-Frame Photo Sequence (Authentic Multi-Angle) */
                <div
                    style={{
                        position: 'relative',
                        width: '84%',
                        height: '84%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: `scale(${zoomLevel}) translate(${panOffset.x}px, ${panOffset.y}px)`,
                        transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                        zIndex: 2
                    }}
                >
                    <img
                        src={activeFrameSrc}
                        alt={`${productName} - 360 View`}
                        draggable={false}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 15px 25px rgba(0,0,0,0.6))',
                            pointerEvents: 'none'
                        }}
                    />
                </div>
            ) : (
                /* Mode 2: Single Image Smart Turntable (Preserving Authentic Product Details) */
                <div
                    style={{
                        position: 'relative',
                        width: '82%',
                        height: '82%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: `scale(${zoomLevel}) perspective(1000px) rotateY(${rotationAngle}deg)`,
                        transformStyle: 'preserve-3d',
                        transition: isDragging ? 'none' : 'transform 0.12s ease-out',
                        zIndex: 2
                    }}
                >
                    {/* Front Face of the Real Product */}
                    <img
                        src={activeFrameSrc}
                        alt={`${productName} - 360 Front`}
                        draggable={false}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.65))',
                            backfaceVisibility: 'visible',
                            pointerEvents: 'none'
                        }}
                    />
                </div>
            )}

            {/* Top Badges: 360 Indicator & Angle Degree */}
            <div
                style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    zIndex: 10
                }}
            >
                <div
                    style={{
                        background: 'rgba(11, 14, 20, 0.85)',
                        border: '1px solid rgba(212, 175, 55, 0.4)',
                        color: '#D4AF37',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '11.5px',
                        fontWeight: '800',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        backdropFilter: 'blur(8px)'
                    }}
                >
                    <RotateCw size={13} className={autoRotate ? 'spin-slow' : ''} />
                    <span>360° LIVE VIEWER</span>
                </div>

                <div
                    style={{
                        background: 'rgba(15, 23, 42, 0.75)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#94A3B8',
                        padding: '4px 8px',
                        borderRadius: '16px',
                        fontSize: '11px',
                        fontWeight: '700',
                        backdropFilter: 'blur(8px)'
                    }}
                >
                    {Math.round(activeAngleDeg)}°
                </div>
            </div>

            {/* Help Directional Instruction Banner */}
            <div
                style={{
                    position: 'absolute',
                    bottom: '16px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(11, 14, 20, 0.82)',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#F8FAFC',
                    padding: '6px 14px',
                    borderRadius: '24px',
                    fontSize: '12px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                    pointerEvents: 'none',
                    zIndex: 10
                }}
            >
                <Compass size={14} color="#D4AF37" />
                <span>{isRTL ? '← اسحب بالماوس أو الإصبع للدوران 360° →' : '← Drag to Rotate 360° →'}</span>
            </div>

            {/* Quick Floating Action Controls Bar */}
            <div
                style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    zIndex: 10
                }}
            >
                {/* Auto-Rotate Toggle */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setAutoRotate(!autoRotate);
                    }}
                    title={autoRotate ? (isRTL ? 'إيقاف الدوران' : 'Stop Rotation') : (isRTL ? 'تشغيل الدوران التلقائي' : 'Auto Rotate')}
                    style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: autoRotate ? 'linear-gradient(135deg, #D4AF37 0%, #AA771C 100%)' : 'rgba(15, 23, 42, 0.85)',
                        color: autoRotate ? '#0B0E14' : '#D4AF37',
                        border: '1px solid rgba(212, 175, 55, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        transition: 'all 0.2s ease'
                    }}
                >
                    {autoRotate ? <Pause size={16} /> : <Play size={16} />}
                </button>

                {/* Zoom In */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleZoomIn();
                    }}
                    title={isRTL ? 'تكبير (+)' : 'Zoom In'}
                    style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'rgba(15, 23, 42, 0.85)',
                        color: '#F8FAFC',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }}
                >
                    <ZoomIn size={16} />
                </button>

                {/* Zoom Out */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleZoomOut();
                    }}
                    title={isRTL ? 'تصغير (-)' : 'Zoom Out'}
                    style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'rgba(15, 23, 42, 0.85)',
                        color: '#F8FAFC',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }}
                >
                    <ZoomOut size={16} />
                </button>

                {/* Reset View */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleReset();
                    }}
                    title={isRTL ? 'إعادة ضبط المنظور' : 'Reset View'}
                    style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'rgba(15, 23, 42, 0.85)',
                        color: '#F8FAFC',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }}
                >
                    <RefreshCw size={15} />
                </button>

                {/* Fullscreen Toggle */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFullscreen();
                    }}
                    title={isRTL ? 'ملء الشاشة' : 'Fullscreen'}
                    style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'rgba(15, 23, 42, 0.85)',
                        color: '#F8FAFC',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }}
                >
                    <Maximize size={15} />
                </button>
            </div>
        </div>
    );
}
